import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../packages/auth-client';
import { getProjects } from '../packages/api-client/pm-api';
import { Spinner } from '../packages/ui';

/**
 * LandingResolver — decides where a freshly-authenticated user lands.
 *
 * Restricted users (project- or task-restricted) are sent to their project
 * instead of the dashboard: straight into the project if they can access
 * exactly one, otherwise to the (already-scoped) Projects list. Everyone else
 * goes to the dashboard.
 *
 * Used as the default post-login target so the decision happens once the user
 * is loaded, rather than racing the async profile fetch inside LoginPage.
 */
export function LandingResolver() {
    const { user, isLoading, isRestricted } = useAuth();
    const [target, setTarget] = useState<string | null>(null);

    useEffect(() => {
        if (isLoading) return;
        if (!user) { setTarget('/login'); return; }
        if (!isRestricted()) { setTarget('/dashboard'); return; }

        let cancelled = false;
        // limit=2 is enough to tell "exactly one" from "more than one".
        getProjects(1, 2)
            .then(res => {
                if (cancelled) return;
                const items = res.data ?? [];
                const total = res.meta?.total ?? items.length;
                setTarget(total === 1 && items[0] ? `/projects/${items[0].id}` : '/projects');
            })
            .catch(() => { if (!cancelled) setTarget('/projects'); });
        return () => { cancelled = true; };
    }, [user, isLoading, isRestricted]);

    if (!target) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spinner size="lg" />
            </div>
        );
    }
    return <Navigate to={target} replace />;
}
